import React, { useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Paper, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { CalendarEvent } from './SidePanel';
import SidePanel from './SidePanel';
import CustomCalendar, { CalendarView, CalendarEvent as CustomCalendarEvent } from './CustomCalendar';
import { useTaskMutations } from '../../hooks/useTaskData';
import { TasksService } from '../../api/generated/requests/services.gen';
import { useTasksServiceGetApiTasksStartDateBetween } from '../../api/generated/queries/queries';
import '../../api/openApiConfig';
import { TaskDTO } from "../../api";

// Definicja interfejsu TaskEventModel
interface TaskEventModel {
  Id: number;
  Subject: string;
  Description: string;
  StartTime: Date;
  EndTime: Date;
  Status: string;
  Priority: 'A' | 'B' | 'C' | 'D' | 'E';
  TaskType?: 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER';
  assignedToUserId?: string;
  prefixId?: string;
}

// Konwersja z DTO do modelu EventScheduler
const mapTaskDTOToEvent = (task: TaskDTO): TaskEventModel => {
  const endDate = new Date(task.endDate || new Date().toISOString());
  // Ustaw StartTime na godzinę przed EndTime jeśli startDate nie jest podane
  const startTime = task.startDate ? new Date(task.startDate) : new Date(endDate.getTime() - 60 * 60 * 1000);

  return {
    Id: task.id || 0,
    Subject: task.title || '',
    Description: task.description || '',
    StartTime: startTime,
    EndTime: endDate,
    Status: task.status || '',
    Priority: task.priority || 'C',
    TaskType: task.taskType,
    assignedToUserId: task.assignedToUserId,
    prefixId: task.prefixId
  };
};

// Konwersja z modelu EventScheduler do DTO
const mapEventToTaskDTO = (event: TaskEventModel): TaskDTO => {
  return {
    id: event.Id,
    title: event.Subject,
    description: event.Description,
    status: event.Status as 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE',
    priority: event.Priority,
    taskType: event.TaskType || 'EMAIL',
    startDate: event.StartTime.toISOString(),
    endDate: event.EndTime.toISOString(),
    assignedToUserId: event.assignedToUserId,
    prefixId: event.prefixId
  };
};

// Konwersja z TaskEventModel do CustomCalendarEvent
const mapTaskEventToCalendarEvent = (event: TaskEventModel): CustomCalendarEvent => {
  return {
    Id: event.Id,
    Subject: event.Subject,
    Description: event.Description,
    StartTime: event.StartTime,
    EndTime: event.EndTime,
    Status: event.Status,
    Priority: event.Priority,
    TaskType: event.TaskType,
    assignedToUserId: event.assignedToUserId,
    prefixId: event.prefixId
  };
};

// Konwersja z CustomCalendarEvent do CalendarEvent (dla SidePanel)
const mapCustomEventToSidePanelEvent = (event: CustomCalendarEvent): CalendarEvent => {
  return {
    Id: event.Id.toString(),
    Subject: event.Subject,
    StartTime: event.StartTime,
    EndTime: event.EndTime,
    Description: event.Description,
    Status: event.Status,
    Priority: event.Priority,
    TaskType: event.TaskType,
    assignedToUserId: event.assignedToUserId,
    prefixId: event.prefixId
  };
};

interface TaskCalendarProps {
  onTaskSelect: (task: TaskDTO) => void;
  onHourClick?: (date: Date) => void;
  refreshTrigger?: number;
  onTaskUpdate?: (task: TaskDTO) => Promise<void>;
  onTaskDelete?: (taskId: number) => Promise<void>;
  searchTerm?: string;
  statusFilters?: string[];
  priorityFilters?: string[];
  typeFilters?: string[];
  initialDate?: Date;
  initialView?: 'month' | 'week' | 'workWeek' | 'day' | 'agenda';
}

const TaskCalendar: React.FC<TaskCalendarProps> = ({ 
  onTaskSelect, 
  onHourClick,
  refreshTrigger, 
  onTaskUpdate, 
  onTaskDelete,
  searchTerm = '',
  statusFilters = [],
  priorityFilters = [],
  typeFilters = [],
  initialDate,
  initialView
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showSidePanel, setShowSidePanel] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<CalendarView>(initialView || (isMobile ? 'day' : 'week'));
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate || new Date());
  const { enqueueSnackbar } = useSnackbar();
  const { updateTask, deleteTask } = useTaskMutations();

  // Funkcja do obliczania zakresu dat na podstawie widoku i wybranej daty
  const calculateDateRange = useCallback((view: CalendarView, date: Date) => {
    const start = new Date(date);
    const end = new Date(date);

    switch (view) {
      case 'day':
        // Dla widoku dziennego - od początku dnia do końca dnia
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
      case 'workWeek':
        // Dla widoku tygodniowego - od poniedziałku do niedzieli
        const dayOfWeek = start.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Poniedziałek jako pierwszy dzień tygodnia
        start.setDate(start.getDate() + mondayOffset);
        start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        // Dla widoku miesięcznego - od pierwszego dnia miesiąca do ostatniego
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(end.getMonth() + 1, 0); // Ostatni dzień miesiąca
        end.setHours(23, 59, 59, 999);
        break;
      case 'agenda':
        // Dla widoku agendy - następne 30 dni
        start.setHours(0, 0, 0, 0);
        end.setDate(end.getDate() + 30);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        // Domyślnie widok tygodniowy
        const defaultDayOfWeek = start.getDay();
        const defaultMondayOffset = defaultDayOfWeek === 0 ? -6 : 1 - defaultDayOfWeek;
        start.setDate(start.getDate() + defaultMondayOffset);
        start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  }, []);

  // Oblicz zakres dat dla aktualnego widoku i daty
  const dateRange = calculateDateRange(currentView, selectedDate);

  // Pobierz zadania z API na podstawie zakresu dat
  const { 
    data: tasks = [], 
    isLoading, 
    error,
    refetch 
  } = useTasksServiceGetApiTasksStartDateBetween(
    {
      start: dateRange.start,
      end: dateRange.end
    },
    undefined,
    {
      enabled: true,
      staleTime: 30000, // 30 sekund
      refetchOnWindowFocus: false
    }
  );

  // Obsługa błędów API
  useEffect(() => {
    if (error) {
      console.error('Błąd podczas pobierania zadań:', error);
      enqueueSnackbar('Wystąpił błąd podczas pobierania zadań', { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  // Konwersja zadań do formatu wydarzeń kalendarza
  const events: TaskEventModel[] = tasks.map(mapTaskDTOToEvent);

  // Odświeżanie danych po otrzymaniu triggera odświeżenia
  useEffect(() => {
    if (refreshTrigger) {
      // Refetch tasks when refresh trigger changes
      refetch();
      // Note: No need to dispatch global event here as TaskDialog already handles it
    }
  }, [refreshTrigger]);

  // Refetch data when view or date changes
  useEffect(() => {
    refetch();
  }, [currentView, selectedDate]);

  // Obsługa kliknięcia na komórkę
  const handleCellClick = (date: Date) => {
    if (currentView === 'month') {
      // W widoku miesięcznym - przejdź do widoku dziennego
      setCurrentView('day');
      setSelectedDate(date);
      // Powiadom CustomCalendar o zmianie daty i widoku
      handleDateChange(date);
      handleViewChange('day');
    } else if ((currentView === 'day' || currentView === 'week' || currentView === 'workWeek') && onHourClick) {
      // W widoku dziennym/tygodniowym - otwórz dialog tworzenia zadania
      onHourClick(date);
    }
  };

  // Obsługa kliknięcia na wydarzenie
  const handleEventClick = (event: CustomCalendarEvent) => {
    // Otwieramy tylko panel boczny z wybranymi danymi (bez dialogu szczegółów)
    setSelectedEvent(mapCustomEventToSidePanelEvent(event));
    setShowSidePanel(true);
  };

  // Obsługa aktualizacji zadania
  const handleUpdateTask = async (task: TaskDTO): Promise<void> => {
    try {
      if (!task.id) {
        // Jeśli zadanie nie ma ID, tworzymy nowe - używamy bezpośrednio TasksService dla tworzenia
        // ponieważ useTaskMutations nie obsługuje tworzenia nowych zadań
        const newTask = await TasksService.postApiTasks({ requestBody: task });
        enqueueSnackbar(`Zadanie zostało utworzone (ID: ${newTask.prefixId}-${newTask.id})`, { variant: 'success', autoHideDuration: 15000 });

        // Dispatch global event to refresh all tabs after creating new task
        window.dispatchEvent(new CustomEvent('refresh-tasks'));
      } else {
        // W przeciwnym razie aktualizujemy istniejące używając zoptymalizowanego hooka
        updateTask({
          id: task.id,
          requestBody: task
        });

        // Aktualizuj wybrany event w panelu bocznym jeśli jest otwarty
        if (selectedEvent && parseInt(selectedEvent.Id) === task.id) {
          const updatedEvent = mapTaskDTOToEvent(task);
          setSelectedEvent(mapCustomEventToSidePanelEvent(mapTaskEventToCalendarEvent(updatedEvent)));
        }
      }
    } catch (error) {
      console.error('Błąd podczas aktualizacji zadania:', error);
      enqueueSnackbar('Wystąpił błąd podczas zapisywania zadania', { variant: 'error' });
    }
  };

  // Obsługa usuwania zadania
  const handleDeleteTask = async (taskId: number): Promise<void> => {
    try {
      // Używamy zoptymalizowanego hooka do usuwania zadania
      deleteTask({ id: taskId });

      // Zamknij panel boczny jeśli usuwane zadanie było wybrane
      if (selectedEvent && parseInt(selectedEvent.Id) === taskId) {
        setShowSidePanel(false);
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error('Błąd podczas usuwania zadania:', error);
      enqueueSnackbar('Wystąpił błąd podczas usuwania zadania', { variant: 'error' });
    }
  };

  // Zamknięcie panelu bocznego
  const handleCloseSidePanel = () => {
    setShowSidePanel(false);
    setSelectedEvent(null);
  };

  // Zapisanie zmian w wydarzeniu
  const handleSaveEvent = async (updatedEvent: CalendarEvent) => {
    try {
      // Konwersja do modelu zadania
      const taskModel: TaskEventModel = {
        Id: parseInt(updatedEvent.Id),
        Subject: updatedEvent.Subject,
        Description: updatedEvent.Description || '',
        StartTime: updatedEvent.StartTime,
        EndTime: updatedEvent.EndTime,
        Status: updatedEvent.Status || 'TODO',
        Priority: updatedEvent.Priority || 'C',
        TaskType: updatedEvent.TaskType
      };

      // Konwersja do TaskDTO i aktualizacja
      const taskDTO = mapEventToTaskDTO(taskModel);
      await handleUpdateTask(taskDTO);

      setShowSidePanel(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Błąd podczas aktualizacji wydarzenia:', error);
      enqueueSnackbar('Wystąpił błąd podczas aktualizacji wydarzenia', { variant: 'error' });
    }
  };

  // Usunięcie wydarzenia
  const handleDeleteEvent = async (eventId: string) => {
    try {
      const idToDelete = parseInt(eventId);
      await handleDeleteTask(idToDelete);
      setShowSidePanel(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Błąd podczas usuwania wydarzenia:', error);
      enqueueSnackbar('Wystąpił błąd podczas usuwania wydarzenia', { variant: 'error' });
    }
  };

  // Szybka aktualizacja zadania
  const handleQuickTaskUpdate = async (taskId: number, updates: Partial<CalendarEvent>): Promise<void> => {
    try {
      // Znajdź zadanie do zaktualizowania
      const taskToUpdate = events.find(event => event.Id === taskId);
      if (!taskToUpdate) return;

      // Przygotuj zaktualizowane dane zadania
      const updatedTaskData: TaskDTO = {
        id: taskId,
        title: updates.Subject || taskToUpdate.Subject,
        description: updates.Description || taskToUpdate.Description,
        status: (updates.Status || taskToUpdate.Status) as 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE',
        priority: updates.Priority || taskToUpdate.Priority,
        taskType: updates.TaskType || taskToUpdate.TaskType || 'EMAIL',
        startDate: updates.StartTime ? updates.StartTime.toISOString() : taskToUpdate.StartTime.toISOString(),
        endDate: updates.EndTime ? updates.EndTime.toISOString() : taskToUpdate.EndTime.toISOString(),
        assignedToUserId: updates.assignedToUserId !== undefined ? updates.assignedToUserId : taskToUpdate.assignedToUserId
      };

      // Używamy zoptymalizowanego hooka do aktualizacji zadania
      updateTask({
        id: taskId,
        requestBody: updatedTaskData
      });

      // Aktualizuj wybrany event w panelu bocznym
      if (selectedEvent && parseInt(selectedEvent.Id) === taskId) {
        const updatedEvent = { ...selectedEvent, ...updates };
        setSelectedEvent(updatedEvent);
      }
    } catch (error) {
      console.error('Błąd podczas aktualizacji zadania:', error);
      throw error; // Przekaż błąd do SidePanel
    }
  };

  // Otwórz szczegóły zadania
  const handleOpenTaskDetail = (taskId: number) => {
    // Konwertuj taskId na TaskDTO i przekaż do onTaskSelect
    const taskEvent = events.find(event => event.Id === taskId);
    if (taskEvent) {
      const taskDTO = mapEventToTaskDTO(taskEvent);
      onTaskSelect(taskDTO);
    }
    // Zamknij panel boczny
    //setShowSidePanel(false);
    //setSelectedEvent(null);
  };

  // Obsługa przeciągania i zmiany rozmiaru zadań
  const handleEventUpdate = async (eventId: number, updates: { startTime: Date; endTime: Date }): Promise<void> => {
    // Znajdź zadanie do zaktualizowania
    const taskToUpdate = events.find(event => event.Id === eventId);
    if (!taskToUpdate) {
      throw new Error('Zadanie nie zostało znalezione');
    }

    // Update selected event in side panel if open (optimistic update)
    if (selectedEvent && parseInt(selectedEvent.Id) === eventId) {
      setSelectedEvent(prev => prev ? {
        ...prev,
        StartTime: updates.startTime,
        EndTime: updates.endTime
      } : null);
    }

    // Prepare updated task data for API
    const updatedTaskData: TaskDTO = {
      id: eventId,
      title: taskToUpdate.Subject,
      description: taskToUpdate.Description,
      status: taskToUpdate.Status as 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE',
      priority: taskToUpdate.Priority,
      taskType: taskToUpdate.TaskType || 'EMAIL',
      startDate: updates.startTime.toISOString(),
      endDate: updates.endTime.toISOString(),
      assignedToUserId: taskToUpdate.assignedToUserId
    };

    // Używamy zoptymalizowanego hooka do aktualizacji zadania
    try {
      updateTask({
        id: eventId,
        requestBody: updatedTaskData
      });
    } catch (error) {
      console.error('Błąd podczas aktualizacji zadania:', error);

      // Revert selected event in side panel if open
      if (selectedEvent && parseInt(selectedEvent.Id) === eventId) {
        setSelectedEvent(prev => prev ? {
          ...prev,
          StartTime: taskToUpdate.StartTime,
          EndTime: taskToUpdate.EndTime
        } : null);
      }

      enqueueSnackbar('Nie udało się zaktualizować zadania', { variant: 'error' });
      throw error;
    }
  };

  // Obsługa zmiany widoku
  const handleViewChange = (view: CalendarView) => {
    setCurrentView(view);
  };

  // Obsługa zmiany daty
  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  if (isLoading && events.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        sx={{
          height: { 
            xs: 'calc(100vh - 200px)',
            sm: 'calc(100vh - 180px)',
            md: 'calc(100vh - 160px)',
            lg: 'calc(100vh - 140px)'
          },
          minHeight: '600px',
          maxHeight: '900px'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Filtrowanie wydarzeń
  const filteredEvents = events.filter(event => {
    // Filtrowanie po wyszukiwanym terminie
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesTitle = event.Subject.toLowerCase().includes(searchLower);
      const matchesDescription = event.Description.toLowerCase().includes(searchLower);
      if (!matchesTitle && !matchesDescription) {
        return false;
      }
    }

    // Filtrowanie po statusie
    if (statusFilters.length > 0 && !statusFilters.includes(event.Status)) {
      return false;
    }

    // Filtrowanie po priorytecie
    if (priorityFilters.length > 0 && !priorityFilters.includes(event.Priority)) {
      return false;
    }

    // Filtrowanie po typie zadania
    if (typeFilters.length > 0 && event.TaskType && !typeFilters.includes(event.TaskType)) {
      return false;
    }

    return true;
  });

  // Konwersja wydarzeń do formatu CustomCalendar
  const calendarEvents: CustomCalendarEvent[] = filteredEvents.map(mapTaskEventToCalendarEvent);

  return (
    <Paper elevation={3} sx={{ 
      height: { 
        xs: 'calc(100vh - 200px)', // Mobile: more space for navigation
        sm: 'calc(100vh - 180px)', // Tablet: current setting
        md: 'calc(100vh - 160px)', // Desktop: less top margin needed
        lg: 'calc(100vh - 140px)'  // Large screens: optimize space usage
      }, 
      minHeight: '600px', // Ensure minimum usable height
      maxHeight: '900px', // Prevent excessive height on very large screens
      overflow: 'hidden' 
    }}>
      <Box sx={{ height: '100%', display: 'flex' }}>
        <Box sx={{ flex: 1, height: '100%' }}>
          <CustomCalendar
            events={calendarEvents}
            currentView={currentView}
            selectedDate={selectedDate}
            onViewChange={handleViewChange}
            onDateChange={handleDateChange}
            onEventClick={handleEventClick}
            onCellClick={handleCellClick}
            onEventUpdate={handleEventUpdate}
            loading={isLoading}
          />
        </Box>

        {showSidePanel && selectedEvent && (
          <Box sx={{ width: 400, borderLeft: '1px solid #e0e0e0' }}>
            <SidePanel
              selectedEvent={selectedEvent}
              onClose={handleCloseSidePanel}
              onSave={handleSaveEvent}
              onDelete={handleDeleteEvent}
              onTaskUpdate={handleQuickTaskUpdate}
              onOpenTaskDetail={handleOpenTaskDetail}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default TaskCalendar;
